<template>
  <div>
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="$store.state.currentUser.id" />
    <edu-yearly-notification v-if="$store.state.currentUser.id" />
    <div class="user-community-page">
      <page-breadcrumb
        :category="$i18nPresenter.t('workspace')"
        :text="breadCrumbText"
      />
      <div class="flex flex-col justify-center user-community-content">
        <div class="flex flex-wrap justify-center w-full mx-auto">
          <div class="flex flex-wrap justify-center w-full community-link lg:flex-nowrap">
            <div class="community-section">
              <aha-section>
                <template #header>
                  <h5>{{ $i18nPresenter.t('Community') }}</h5>
                </template>
                <template #body>
                  <div class="flex flex-col justify-center">
                    <p class="mb-2 text-center">
                      {{ $i18nPresenter.t('Join our community:') }}
                    </p>
                    <p class="text-center">
                      <a
                        href="https://www.facebook.com/groups/AhaSlides/"
                        target="_blank"
                        title="AhaSlides Facebook group"
                        class="ml-1 mr-2 community__link community__link-facebook"
                        @click="trackGA('What\'s New > Community > Facebook group')"
                      >
                        <i class="fab fa-facebook-square fa-fw" />
                        <span class="pl-1 community__link-facebook-text">{{ $i18nPresenter.t('Facebook group') }}</span>
                      </a>
                      <a
                        href="https://www.reddit.com/r/ahaslides"
                        target="_blank"
                        title="AhaSlides’ subreddit on reddit.com"
                        class="community__link community__link-reddit"
                        @click="trackGA('What\'s New > Community > Reddit')"
                      >
                        <i class="fab fa-reddit fa-fw" />
                        <span class="pl-1 community__link-reddit-text">r/ahaslides</span>
                      </a>
                    </p>
                  </div>
                </template>
              </aha-section>
            </div>
            <div class="social-section">
              <aha-section>
                <template #header>
                  <h5>{{ $i18nPresenter.t('Social media') }}</h5>
                </template>
                <template #body>
                  <div class="flex flex-col justify-center">
                    <p class="mb-2 text-center">
                      {{ $i18nPresenter.t('Follow us on:') }}
                    </p>
                    <p class="text-center">
                      <a
                        href="https://twitter.com/AhaSlides"
                        target="_blank"
                        title="Twitter"
                        class="ml-1 mr-2 community__link community__link-twitter"
                        @click="trackGA('What\'s New > Social media > Twitter')"
                      >
                        <i class="fab fa-twitter fa-fw" />
                      </a>
                      <a
                        href="https://www.facebook.com/AhaSlides"
                        target="_blank"
                        title="Facebook"
                        class="mr-2 community__link community__link-facebook"
                        @click="trackGA('What\'s New > Social media > Facebook')"
                      >
                        <i class="fab fa-facebook-square fa-fw" />
                      </a>
                      <a
                        href="https://www.instagram.com/ahaslides/"
                        target="_blank"
                        title="Instagram"
                        class="mr-2 community__link community__link-instagram fa-fw"
                        @click="trackGA('What\'s New > Social media > Instagram')"
                      >
                        <i class="fab fa-instagram" aria-hidden="true" />
                      </a>
                      <a
                        href="https://www.youtube.com/channel/UC6ZZtv3C9bveCE8E8tBPhPA"
                        target="_blank"
                        title="YouTube"
                        class="mr-2 community__link community__link-youtube"
                        @click="trackGA('What\'s New > Social media > YouTube')"
                      >
                        <i class="fab fa-youtube fa-fw" />
                      </a>
                      <a
                        href="https://www.linkedin.com/company/ahaslides/"
                        target="_blank"
                        title="LinkedIn"
                        class="mr-2 community__link community__link-linkedin"
                        @click="trackGA('What\'s New > Social media > LinkedIn')"
                      >
                        <i class="fab fa-linkedin-in fa-fw" />
                      </a>
                      <a
                        href="https://ahaslides.medium.com/"
                        target="_blank"
                        title="Medium"
                        class="community__link community__link-medium"
                        @click="trackGA('What\'s New > Social media > Medium')"
                      >
                        <i class="fab fa-medium fa-fw" />
                      </a>
                    </p>
                  </div>
                </template>
              </aha-section>
            </div>
          </div>
          <!-- Twitter timeline -->
          <div class="w-full h-1/2">
            <div class="w-full lg:px-4">
              <aha-section>
                <template #header>
                  <h5>
                    {{ $i18nPresenter.t('Latest from the AhaSlides team') }}
                  </h5>
                </template>
                <template #body>
                  <div>
                    <div v-if="loadingTweets" class="text-center">
                      <aha-spinner />
                    </div>
                    <div v-if="!loadingTweets && errorText" class="text-red-100">
                      {{ errorText }}
                    </div>
                    <div
                      :class="{'hidden': loadingTweets && !errorText}"
                      class="flex flex-col items-center twitter-timeline"
                    >
                      <a
                        id="twitter-timeline"
                        class="twitter-timeline"
                        href="https://twitter.com/AhaSlides"
                        data-chrome="noheader nofooter"
                        data-tweet-limit="20"
                        data-width="425"
                        data-dnt="true"
                      />
                      <div class="mt-6 vertical layout center" v-if="readMoreVisible">
                        <a
                          href="https://twitter.com/ahaslides"
                          target="_blank"
                          class="hover:underline"
                          @click="trackGA('What\'s New > Twitter timeline > Read more tweets')"
                        >{{ $i18nPresenter.t('Read more...') }}
                        </a>
                      </div>
                    </div>
                  </div>
                </template>
              </aha-section>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Utils from '@/util'
import constants from '@/constant'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import EduYearlyNotification from '@/components-v2/common/global-notification/EduYearlyNotification.vue'
import PageBreadcrumb from '../common/page-breadcrumb/PageBreadcrumb.vue'

export default {
  components: {
    PageBreadcrumb,
    InvitationNotifications,
    EduYearlyNotification,
    PublicAnnouncement,
  },
  props: {
    src: {
      type: String,
      default: 'https://twitter.com/vuejs?ref_src=twsrc%5Etfw',
    },
    dark: {
      type: Boolean,
      default: false,
    },
    dataLinkColor: {
      type: String,
      default: '#6A1EBB', // Primary color
    },
  },
  data() {
    return {
      loadingTweets: true,
      checkElement: null,
      readMoreVisible: false,
      errorLoadTimeline: null,
      errorText: null,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
    breadCrumbText() {
      return this.$i18nPresenter.t('Community')
    },
  },
  mounted() {
    if (this.$store.state.isPresenterWhiteLabel) {
      this.$router.push({ name: 'Presentations' })
      return
    }
    // Add twitter widget script when created
    const twitterFeed = document.createElement('script')
    twitterFeed.setAttribute('src', 'https://platform.twitter.com/widgets.js')
    twitterFeed.async = true
    document.head.appendChild(twitterFeed)
    this.checkTimelineElements()
    this.errorLoadTimeline = setTimeout(() => {
      if (!this.errorText) {
        this.checkTimelineError()
      }
    }, 5000)
  },
  destroyed() {
    if (this.checkElement) {
      clearTimeout(this.checkElement)
    }
    if (this.errorLoadTimeline) {
      clearTimeout(this.errorLoadTimeline)
    }
  },
  methods: {
    trackGA(eventAction) {
      Utils.mixpanelTracking(
        constants.COMMUNITY,
        eventAction,
      )
    },
    renderTwitterTimeLineNew() {
      if (window.twttr) {
        window.twttr.widgets.createTimeline(
          {
            sourceType: 'profile',
            screenName: 'AhaSlides',
          },
          document.getElementById('twitter-timeline'),
          {
            chrome: 'noheader nofooter',
            tweetLimit: 20,
            width: 425,
            dnt: true,
          },
        )
      }
    },
    checkTimelineElements() {
      this.checkElement = setTimeout(() => {
        const renderedTimeline = document.getElementsByClassName('twitter-timeline-rendered')
        const errorTimeline = document.getElementsByClassName('twitter-timeline-error')
        if (renderedTimeline && renderedTimeline.length) {
          setTimeout(() => {
            this.readMoreVisible = true
          }, 500)
          this.loadingTweets = false
        } else if (!renderedTimeline.length && !errorTimeline.length) {
          this.checkTimelineElements()
        } else if (!renderedTimeline.length && errorTimeline.length) {
          this.handleTimelineError()
        }
      }, 1000)
    },
    checkTimelineError() {
      const renderedTimeline = document.getElementsByClassName('twitter-timeline-rendered')
      const errorTimeline = document.getElementsByClassName('twitter-timeline-error')
      if (!renderedTimeline.length && !errorTimeline.length) {
        this.handleTimelineError()
      }
    },
    handleTimelineError() {
      clearTimeout(this.checkElement)
      this.loadingTweets = false
      // this.errorText = 'Something went wrong. Please try again.'
      this.trackGA('What\'s New > Twitter timeline > Load timeline failed')
      this.renderTwitterTimeLineNew()
      this.readMoreVisible = true
    },
  },
}
</script>

<style lang="scss" scoped>
.user-community-page {
  @apply p-4 md:px-10 md:py-6;
}

.community-section {
  @apply w-full lg:w-1/2 lg:px-4;
  min-width: 16rem;
}

.social-section {
  @apply w-full lg:w-1/2 lg:px-4;
  min-width: 16rem;
}

.fab {
    font-size: 18px;
  }
  .community__link {
    white-space: nowrap;
    .fab, .fa-instagram {
      opacity: 0.8;
    }
    &:focus, &:hover {
     .fab {
       opacity: 1;
     }
     .community__link-facebook-text {
       color: #3b5998 !important; // facebook color
     }
     .community__link-reddit-text {
       color: #ff4500 !important; // reddit color
     }
    }
  }
  .community__link-facebook .fab {
    color: #3b5998 !important; // facebook color
  }
  .community__link-reddit .fab {
    color: #ff4500 !important; // reddit color
  }
  .community__link-twitter .fab {
    color: #00aced !important; // twitter color
  }
  .community__link-instagram {
    .fa-instagram {
      background: radial-gradient(
        circle at 30% 107%,#fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%
      );
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  .community__link-youtube .fab {
    color: #b00 !important; // YouTube color
  }
  .community__link-linkedin .fab {
    color: #007bb6 !important; // linkedIn color
  }
  .community__link-medium .fab {
    color: #000000 !important; // medium color //
  }
.community__content, .social-media__content, .twitter-timeline__content {
  display: flex;
  .card-title {
    font-weight: bold;
    text-align: center;
    font-size: 18px;
  }
}

</style>
